import { ArrowLeft, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { getSource, useSitePath } from "../context/SiteContext";
import { toast } from "sonner";
import { api, apiError, toCriteria } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { StepLayout } from "../components/StepLayout";
import { SelectField } from "../components/SelectField";
import { Checkbox } from "../components/ui/checkbox";

const TITLES = ["Mr", "Mrs", "Ms", "Dr", "Prof"].map((t) => ({ value: t, label: t }));

const Field = ({ label, name, form, set, type = "text", required = true, className = "" }) => (
  <label className={`field ${className}`}>
    <span className="field-label">{label}{required && " *"}</span>
    <input type={type} required={required} className="field-input" value={form[name]} onChange={(e) => set({ [name]: e.target.value })} data-testid={`guest-${name.replace("_", "-")}-input`} />
  </label>
);

export default function Details() {
  const { criteria, hotel, room, rate, services, clearItinerary } = useBooking();
  const navigate = useNavigate();
  const sp = useSitePath();
  const { slug } = useParams();
  const [form, setForm] = useState({ title: "Mr", first_name: "", last_name: "", email: "", phone: "", country: "", arrival_time: "", requests: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const booked = useRef(false); // suppress the empty-itinerary redirect once a booking has been made
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  if ((!hotel || !rate) && !booked.current) return <Navigate to={sp("/hotels")} replace />;
  if (!hotel || !rate) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the booking terms");
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", { criteria: toCriteria(criteria), hotel_id: hotel.id, room_id: room.id, rate_id: rate.id, services, guest: form, source: getSource(slug) });
      if (data.reservation) {
        booked.current = true;
        clearItinerary();
        navigate(sp(`/confirmation/${data.reservation.ref}`), { state: { reservation: data.reservation } });
      } else {
        navigate(sp(`/payment/${data.checkout.cid}`));
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const actions = (
    <div className="space-y-3">
      <button type="submit" form="guest-form" disabled={busy} className="btn-gold w-full" data-testid="confirm-reservation-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirm reservation
      </button>
      <button className="btn-outline w-full" onClick={() => navigate(sp("/services"))} data-testid="details-back-button"><ArrowLeft className="h-4 w-4" />Back</button>
    </div>
  );

  return (
    <StepLayout step={4} sidebarActions={actions} mobileBar={{ label: "Confirm", form: "guest-form", busy }}>
      <h1 className="font-serif text-4xl text-ink sm:text-5xl">Guest details</h1>
      <p className="mt-2 text-sm text-ink/60">Who should we expect? We'll send the confirmation to this email.</p>
      <form id="guest-form" onSubmit={submit} className="mt-8 grid gap-5 rounded-3xl border border-brand/10 bg-white p-6 sm:grid-cols-6 sm:p-8" data-testid="guest-form">
        <SelectField label="Title" value={form.title} options={TITLES} onChange={(v) => set({ title: v })} testid="guest-title-select" className="sm:col-span-2" />
        <Field label="First name" name="first_name" form={form} set={set} className="sm:col-span-2" />
        <Field label="Last name" name="last_name" form={form} set={set} className="sm:col-span-2" />
        <Field label="Email" name="email" type="email" form={form} set={set} className="sm:col-span-3" />
        <Field label="Phone" name="phone" type="tel" form={form} set={set} className="sm:col-span-3" />
        <Field label="Country of residence" name="country" form={form} set={set} required={false} className="sm:col-span-3" />
        <Field label="Estimated arrival time" name="arrival_time" type="time" form={form} set={set} required={false} className="sm:col-span-3" />
        <label className="field sm:col-span-6">
          <span className="field-label">Special requests</span>
          <textarea rows={4} className="field-input !h-auto py-3" value={form.requests} onChange={(e) => set({ requests: e.target.value })} data-testid="guest-requests-input" />
        </label>
        <label className="flex cursor-pointer items-start gap-3 text-sm text-ink/70 sm:col-span-6">
          <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="guest-terms-checkbox" />
          I accept the booking terms and the cancellation policy of the selected rate.
        </label>
      </form>
    </StepLayout>
  );
}
